import { useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import "./OrderCard.css";

/* ─── Types ──────────────────────────────────────────────── */
interface Product {
  id: number;
  name: string;
  description: string;
  images: string[];
}

interface OrderItem {
  id: number;
  priceAtOrder: number;
  quantity: number;
  size?: string;
  product: Product;
}

export interface Order {
  id: number;
  createdAt: string;
  items: OrderItem[];
  shippingAddress: string;
  status: string;
  totalAmount: number;
}

/* ─── Status Config ──────────────────────────────────────── */
const STATUS_CONFIG: Record<string, { label: string; bg: string; dot: string }> = {
  out_for_delivery: { label: "Out for Delivery", bg: "rgba(102,126,234,0.1)",  dot: "#667eea" },
  delivered:        { label: "Delivered",        bg: "rgba(72,187,120,0.12)",  dot: "#38a169" },
  processing:       { label: "Processing",       bg: "rgba(237,137,54,0.12)",  dot: "#dd6b20" },
  cancelled:        { label: "Cancelled",        bg: "rgba(229,62,62,0.1)",    dot: "#c53030" },
  pending:          { label: "Pending",           bg: "rgba(160,174,192,0.15)", dot: "#718096" },
};

/* ─── Helpers ────────────────────────────────────────────── */
const fmt = (n: number) => `₹${(n ?? 0).toLocaleString("en-IN")}`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/* ─── OrderCard ──────────────────────────────────────────── */
export default function OrderCard({ order }: { order: Order }) {
  const [expanded, setExpanded] = useState(false);

  const cfg = STATUS_CONFIG[order.status] ?? {
    label: order.status,
    bg: "rgba(160,174,192,0.15)",
    dot: "#718096",
  };

  const visibleItems = expanded ? order.items : order.items.slice(0, 2);
  const hiddenCount = order.items.length - 2;
  const totalQty = order.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="oc-card">

      {/* Accent stripe */}
      <div className="oc-bar" />

      {/* Header — date left, status right */}
      <div className="oc-header">
        <span className="oc-date">{fmtDate(order.createdAt)}</span>

        <div className="oc-status" style={{ background: cfg.bg, color: cfg.dot }}>
          <span className="oc-dot" style={{ background: cfg.dot }} />
          {cfg.label}
        </div>
      </div>

      {/* Items */}
      <div className="oc-items">
        {visibleItems.map((item) => {
          const img = item.product?.images?.[0]
            ? `https://api.flooron.in/${item.product.images[0]}`
            : null;
          return (
            <div className="oc-item" key={item.id}>

              {/* Thumbnail */}
              <div className="oc-thumb">
                {img ? <img src={img} alt={item.product.name} /> : "📦"}
              </div>

              {/* Name + desc */}
              <div className="oc-item-info">
                <div className="oc-item-name">{item.product.name}</div>
                <div className="oc-item-desc">
                  {item.product.description}
                  {item.size && (
                    <span className="oc-item-size"> • Size: {item.size}</span>
                  )}
                </div>
              </div>

              {/* Qty + line total */}
              <div className="oc-item-pricing">
                <div className="oc-item-qty">{item.quantity} × {fmt(item.priceAtOrder)}</div>
                <div className="oc-item-total">{fmt(item.quantity * item.priceAtOrder)}</div>
              </div>

            </div>
          );
        })}

        {hiddenCount > 0 && (
          <button className="oc-toggle" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "▲ Show less" : `▼ ${hiddenCount} more item${hiddenCount > 1 ? "s" : ""}`}
          </button>
        )}
      </div>

      {/* Footer — address left, total right */}
      <div className="oc-footer">
        <div className="oc-address">
          <FaMapMarkerAlt className="oc-address-pin" />
          <span className="oc-address-text">{order.shippingAddress}</span>
        </div>

        <div className="oc-total-block">
          <div className="oc-total-label">{totalQty} item{totalQty > 1 ? "s" : ""} · Total</div>
          <div className="oc-total-val">{fmt(order.totalAmount)}</div>
        </div>
      </div>

    </div>
  );
}